/*
  Interaction event tracking.

  Pushes the custom events from the tracking sheet to window.dataLayer. GA4 funnel
  events (view_item, add_to_cart, begin_checkout, purchase, ...) come from Shopify's
  own analytics and are not duplicated here - the quick buy events fire *on top of*
  the existing add_to_cart, as specified in the sheet.

  Set window.trackingDebug = true in the console to log every push.
*/
(function () {
  window.dataLayer = window.dataLayer || [];

  var template = (window.theme && window.theme.template) || '';
  var collectionTitle = (window.theme && window.theme.collectionTitle) || '';
  var collectionHandle = (window.theme && window.theme.collectionHandle) || '';
  // Shopify prefixes non-primary locales onto every path ("/en/products/x"), which
  // would split one link into a row per language. Strip it so link_url is comparable.
  var localeRoot = (window.theme && window.theme.routes && window.theme.routes.root) || '/';

  // Selectors for markup the theme does not render can be corrected from Theme
  // settings > Scripts without a deploy. An empty or whitespace setting keeps the
  // default below, so a blank field can never silently disable an event.
  var overrides = (window.theme && window.theme.trackingSelectors) || {};
  function sel(key, fallback) {
    var value = overrides[key];
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }

  // Empty unless a merchant has set one, so the default scoping stays in charge.
  var cartDiscounts = sel('cart_discounts', '');

  // First matching rule wins, so the more specific areas are listed first.
  // area      - the element must sit inside this selector (omit for site-wide)
  // areaIndex - the matched area must be the nth of its kind on the page
  // ordinal   - {ordinal} in the event name and list is replaced with the area's
  //             position on the page (first, second, third...), so repeated sections
  //             name themselves rather than being hardcoded.
  // trigger   - the clicked element, or one of its ancestors, matches this selector
  // list      - product list the interaction belongs to, added to the GA4 payload
  // media     - also report the clicked image's url and alt text
  // on        - event type, defaults to click
  var RULES = [
    // Cart. The drawer renders on every template, so it is matched first.
    // The wrapper also holds cart notes and gift notes, so by default this is scoped to
    // the widget that renders #discounts - that widget only exists once the cart has
    // something in it. A selector set in theme settings replaces both parts, since an
    // override is assumed to be specific enough on its own.
    {
      event: 'cart_discounts_tab',
      area: cartDiscounts ? null : '.cart__widget:has(#discounts)',
      trigger: cartDiscounts || 'button.cart__widget__title[data-collapsible-trigger]'
    },
    {
      event: 'cart_upsell_add_to_cart',
      area: '#cartUpsellHolder',
      trigger: '.product-upsell__btn, [data-add-to-cart]',
      list: { id: 'cart_upsell', name: 'Cart upsell' }
    },
    {
      event: 'cart_upsell_click',
      area: '#cartUpsellHolder',
      trigger: 'a[href*="/products/"]',
      list: { id: 'cart_upsell', name: 'Cart upsell' }
    },

    // Header and mobile menu.
    {
      event: 'menu_interaction',
      area: '.site-header, [data-mobile-menu], .mobile-menu',
      trigger: 'a[href], button'
    },

    // Skincare test and consultation CTAs, identified by destination.
    { event: 'homepage_skincare_test_CTA', template: 'index',
      trigger: 'a[href*="hudvardstest"]' },
    { event: 'homepage_skin_consultation_CTA', template: 'index',
      trigger: 'a[href*="hudkonsultation"], [onclick*="booxiController"]' },
    { event: 'product_skincare_test_CTA', template: 'product',
      trigger: 'a[href*="hudvardstest"]' },
    { event: 'product_skin_consultation_CTA', template: 'product',
      trigger: 'a[href*="hudkonsultation"], [onclick*="booxiController"]' },

    // Homepage. The tabbed collection section carries its own event; the plain
    // collection sections are the carousels the sheet numbers first/second/third.
    // Tab items are <li class="tabs__link" data-tabs-link>, not links or buttons.
    { event: 'homepage_products_tabs', template: 'index',
      area: '.tab-collections, [id*="section-tab-collections"]',
      trigger: '.tabs__link, [data-tabs-link]' },
    // The homepage renders four banner sections. areaIndex pins this to the first, so
    // the lower banners do not report as the hero - drop areaIndex to count them all.
    { event: 'homepage_hero_banner_cta', template: 'index',
      area: 'banner-image-component', areaIndex: 0,
      trigger: '.banner-image__btn' },
    {
      event: 'homepage_{ordinal}_product_carousel_quick_buy',
      template: 'index', area: '[id^="shopify-section-"]:has(.featured-collection__container)', ordinal: true,
      trigger: '.product-grid-item__quick-buy, .btn--quick-buy, [data-add-to-cart]',
      list: { id: 'homepage_{ordinal}_product_carousel', name: 'Homepage {ordinal} carousel' }
    },
    {
      event: 'homepage_{ordinal}_product_carousel_image_click',
      template: 'index', area: '[id^="shopify-section-"]:has(.featured-collection__container)', ordinal: true,
      trigger: '[data-product-image], .product__media__image', media: true,
      list: { id: 'homepage_{ordinal}_product_carousel', name: 'Homepage {ordinal} carousel' }
    },
    {
      event: 'homepage_{ordinal}_product_carousel_all_link',
      template: 'index', area: '[id^="shopify-section-"]:has(.featured-collection__container)', ordinal: true,
      trigger: '.featured-collection__button a, .featured-collection__button, [class*="ai-featured-collection__button-"]'
    },

    // Collection.
    // Two sections can render the category strip on a collection page: subcollections
    // (image cards) and the buttons list (list-collections). Both are configured
    // per collection, so neither renders unless someone has picked collections for it.
    { event: 'collections_tabs', template: 'collection',
      trigger: '.subcollection__image, a.list-collections__item__outer, .tabs__link, [data-tabs-link]' },
    {
      event: 'collections_quick_buy',
      template: 'collection',
      trigger: '.product-grid-item__quick-buy, .btn--quick-buy, [data-add-to-cart]',
      list: { id: collectionHandle || 'collection', name: collectionTitle || 'Collection' }
    },

    // Product upsell / related products at the end of the product page.
    { event: 'product_upsell_tabs', template: 'product',
      area: '.related-products, [id*="related-products"]',
      trigger: '.tabs__link, [data-tabs-link]' },
    {
      event: 'product_upsell_quick_buy',
      area: '.related-products, [id*="related-products"], .recently-viewed',
      trigger: '.product-grid-item__quick-buy, .btn--quick-buy, [data-add-to-cart]',
      list: { id: 'product_upsell', name: 'Product upsell' }
    },
    {
      event: 'product_upsell_image_click',
      area: '.related-products, [id*="related-products"], .recently-viewed',
      trigger: '[data-product-image], .product__media__image', media: true,
      list: { id: 'product_upsell', name: 'Product upsell' }
    },

    // Product page.
    // Thumbnails carry data-id (the media id), not data-media-id - that lives on the
    // main slider's slides. Confirmed against the rendered product page.
    { event: 'product_image_gallery_click', area: '[data-product-single-media-thumbs]',
      trigger: '.product-single__thumbnail, [data-thumbnail-id]', media: true },
    { event: 'product_image_click', area: '[data-product-single-media-slider]',
      trigger: '[data-product-single-media-wrapper] img, .product__media img', media: true },
    { event: 'product_reviews_rating_click', template: 'product',
      area: 'product-component',
      trigger: sel('reviews_badge', '[data-widget-name="preview_badge"]') },
    // Subscriptions come from Recharge, which fills .recharge-subscription-widget at
    // runtime - the theme's own selling-plan form is never rendered on this store, so
    // the option markup is the app's. Both selectors are correctable in theme settings.
    // The one-time default is intentionally narrow: until someone supplies the real
    // selector it matches nothing rather than mislabelling every plan choice.
    { event: 'product_one-time_click', template: 'product', on: 'change',
      area: '.recharge-subscription-widget, [data-subscription-selectors]',
      trigger: sel('subscription_onetime', '[data-rc-one-time], input[value=""]') },
    { event: 'product_subscribe_click', template: 'product', on: 'change',
      area: '.recharge-subscription-widget, [data-subscription-selectors]',
      trigger: sel('subscription_plan', 'input[type="radio"], select') },
    // This theme shows product info as accordions, not tabs - the only tabs-component
    // instances on the PDP are the size-chart popups.
    { event: 'product_first_tabs', template: 'product',
      trigger: '.product__accordion__title' },
    // "Buy with" is the upsell block on the product page. complementary-products is the
    // Search & Discovery variant of the same idea, so both are covered.
    { event: 'product_buy_with_add_to_cart', area: '.upsell__products, .complementary-products',
      trigger: '.product-upsell__btn, [data-add-to-cart]' },
    { event: 'product_buy_with_click', area: '.upsell__products, .complementary-products',
      trigger: 'a[href*="/products/"]', media: true },
    // Standalone accordion sections on the product page (key ingredients, in-the-kit).
    { event: 'product_second_tabs', template: 'product',
      area: 'collapsible-elements.image-with-text__content',
      trigger: '[data-collapsible-trigger]' },
    // The section type keeps this off the cart notes and gift-note widgets, which use
    // the same collapsible trigger.
    { event: 'product_faqs', area: '[data-section-type="accordions"]',
      trigger: '[data-collapsible-trigger]' },

    // Judge.me review widget. Its markup is injected by the app rather than rendered by
    // the theme, so these selectors are confirmed against the live page, not the repo.
    // Sorting is a <select>, so it reports on change rather than click.
    { event: 'product_sorting_reviews', template: 'product', on: 'change',
      trigger: sel('reviews_sort', '.jdgm-sort-dropdown') },
    { event: 'product_load_more_reviews', template: 'product',
      trigger: sel('reviews_load_more', '.jdgm-paginate__load-more') }
  ];

  var ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh',
                  'eighth', 'ninth', 'tenth'];

  function ordinalFor(element, rule) {
    var area = element.closest(rule.area);
    if (!area) return null;
    var all = document.querySelectorAll(rule.area);
    return ORDINALS[Array.prototype.indexOf.call(all, area)] || null;
  }

  // The GA4 item payload rendered by snippets/gtm-product_item.liquid. Nearest wins,
  // so an upsell card beats the product page it sits on.
  function itemFor(element) {
    var source = element.closest('[data-track-item]');
    if (!source) return null;
    try {
      return JSON.parse(source.dataset.trackItem);
    } catch (e) {
      return null;
    }
  }

  // link_text is the visible label, so it changes per locale and is not safe to group
  // on; link_id and link_url survive translation.
  // "/en/products/x" -> "/products/x" so the same link groups across locales.
  function stripLocale(url) {
    if (!url || localeRoot === '/' || url.indexOf(localeRoot) !== 0) return url;
    return '/' + url.slice(localeRoot.length);
  }

  function identityFor(element) {
    var identity = {};
    if (element.tagName === 'SELECT' && element.value) {
      identity.link_id = element.value;
      return identity;
    }
    if (element.tagName === 'INPUT' && element.value) identity.link_id = element.value;
    var tagged = element.closest('[data-track-id]');
    if (tagged && tagged.dataset.trackId) identity.link_id = tagged.dataset.trackId;
    // Product media carries no handle, but the theme already stamps the Shopify media
    // id on each slide and thumbnail - enough to say which image was clicked.
    if (!identity.link_id) {
      // Slides and thumbnails both carry data-id (the Shopify media id). data-media-id
      // on a slide is "{section}-{media id}", so data-id is the one to report.
      var media = element.closest('[data-id], [data-media-id]');
      if (media) {
        identity.link_id = media.dataset.id || media.dataset.mediaId;
      }
    }
    var anchor = element.closest('a[href]');
    if (anchor) identity.link_url = stripLocale(anchor.getAttribute('href'));
    return identity;
  }

  function mediaFor(element) {
    var img = element.tagName === 'IMG' ? element : element.querySelector('img');
    if (!img) return null;
    var media = {};
    var url = img.getAttribute('data-original-src') || img.currentSrc || img.getAttribute('src');
    if (url) media.image_url = url;
    var alt = img.getAttribute('alt');
    if (alt) media.image_alt = alt;
    return media;
  }

  function labelFor(element) {
    // A radio or checkbox has no text of its own; its <label> carries it.
    if (element.tagName === 'INPUT') {
      var label = element.closest('label');
      if (label) return (label.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
    }
    if (element.tagName === 'SELECT') {
      var chosen = element.options[element.selectedIndex];
      return chosen ? (chosen.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '';
    }
    var labelled = element.closest('[data-track-label]');
    if (labelled && labelled.dataset.trackLabel) {
      return labelled.dataset.trackLabel.slice(0, 100);
    }
    var card = element.closest('.product-grid-item, [data-product-block]');
    var title = card && card.querySelector('.product-grid-item__title');
    var source = title || element;
    var value = (source.textContent || '').replace(/\s+/g, ' ').trim();
    return (value || element.getAttribute('aria-label') || '').slice(0, 100);
  }

  function push(name, linkText, item, list, identity, media) {
    var payload = { event: name };
    if (linkText) payload.link_text = linkText;
    if (identity) {
      if (identity.link_id) payload.link_id = identity.link_id;
      if (identity.link_url) payload.link_url = identity.link_url;
    }
    if (media) {
      if (media.image_url) payload.image_url = media.image_url;
      if (media.image_alt) payload.image_alt = media.image_alt;
    }

    if (item) {
      var ecommerce = {};
      if (list) {
        item.item_list_id = list.id;
        item.item_list_name = list.name;
        ecommerce.item_list_id = list.id;
        ecommerce.item_list_name = list.name;
      }
      ecommerce.items = [item];
      payload.ecommerce = ecommerce;
      // Clear the previous ecommerce object so GA4 does not merge stale items in.
      window.dataLayer.push({ ecommerce: null });
    }

    window.dataLayer.push(payload);
    if (window.trackingDebug) console.log('[tracking]', payload);
  }

  // Capture phase on document: immune to any handler calling stopPropagation, works on
  // markup injected after load (the review widget, quick view, cart drawer re-renders),
  // and carries no dependency on the theme's own JS bundles.
  function dispatch(event, type) {
    var target = event.target;
    if (!target || !target.closest) return;

    for (var i = 0; i < RULES.length; i++) {
      var rule = RULES[i];
      if ((rule.on || 'click') !== type) continue;
      if (rule.template && rule.template !== template) continue;

      var element = target.closest(rule.trigger);
      if (!element) continue;
      if (rule.area && !element.closest(rule.area)) continue;
      if (rule.areaIndex !== undefined) {
        var scope = element.closest(rule.area);
        var peers = document.querySelectorAll(rule.area);
        if (Array.prototype.indexOf.call(peers, scope) !== rule.areaIndex) continue;
      }

      var name = rule.event;
      var list = rule.list;
      if (rule.ordinal) {
        var word = ordinalFor(element, rule);
        if (!word) continue;
        name = name.replace('{ordinal}', word);
        if (list) {
          list = {
            id: list.id.replace('{ordinal}', word),
            name: list.name.replace('{ordinal}', word)
          };
        }
      }

      push(
        name,
        labelFor(element),
        itemFor(element),
        list,
        identityFor(element),
        rule.media ? mediaFor(element) : null
      );
      return;
    }
  }

  // --- Cart API events -------------------------------------------------------
  //
  // Shopify's native GA4 integration reports add_to_cart straight to GA4 and never
  // writes to dataLayer, so without this GTM sees the quick-buy click but never the
  // cart change behind it - no quantity, no value, no line item.
  //
  // The theme adds to cart with fetch(), so wrapping fetch catches every path: the
  // product form, quick buy, upsells and the cart drawer's own quantity controls.
  //
  // Named *_custom so it cannot be confused with the native add_to_cart if both are
  // running. Drop the suffix once you have decided which source GA4 should count.

  var currency = (window.theme && window.theme.current_iso_code) || '';

  // Built from the cart response alone. product_id, variant_id and sku come straight
  // from Shopify and are identical in every locale; the titles are display only.
  function cartItem(line) {
    var unit = line.final_price != null ? line.final_price : line.price;
    var item = {
      item_id: line.sku || String(line.variant_id),
      product_id: line.product_id,
      variant_id: line.variant_id,
      item_name: line.product_title || line.title,
      item_handle: line.handle,
      item_variant: line.variant_title,
      price: Math.round(unit) / 100,
      quantity: line.quantity
    };
    if (currency) item.currency = currency;
    return item;
  }

  function pushCart(name, lines) {
    if (!lines || !lines.length) return;
    var items = [];
    var value = 0;
    for (var i = 0; i < lines.length; i++) {
      var item = cartItem(lines[i]);
      value += item.price * (item.quantity || 1);
      items.push(item);
    }
    window.dataLayer.push({ ecommerce: null });
    var payload = {
      event: name,
      ecommerce: { value: Math.round(value * 100) / 100, items: items }
    };
    if (currency) payload.ecommerce.currency = currency;
    window.dataLayer.push(payload);
    if (window.trackingDebug) console.log('[tracking]', payload);
  }

  function handleCartResponse(url, json) {
    if (!json) return;
    if (/\/cart\/add/.test(url)) {
      // add.js returns the added line, or {items:[...]} for a multi-add.
      pushCart('add_to_cart_custom', json.items || [json]);
      return;
    }
    // change.js and update.js return the whole cart plus what moved.
    if (json.items_added && json.items_added.length) {
      pushCart('add_to_cart_custom', json.items_added);
    }
    if (json.items_removed && json.items_removed.length) {
      pushCart('remove_from_cart_custom', json.items_removed);
    }
  }

  (function interceptCart() {
    if (typeof window.fetch !== 'function') return;
    var original = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = original.apply(this, arguments);
      if (!/\/cart\/(add|change|update)/.test(url)) return result;
      return result.then(function (response) {
        // Read a clone so the theme still gets an unconsumed body, and never let a
        // tracking failure surface as a cart failure.
        try {
          response.clone().json().then(function (json) {
            try { handleCartResponse(url, json); } catch (e) {}
          }, function () {});
        } catch (e) {}
        return response;
      });
    };
  })();

  document.addEventListener('click', function (event) { dispatch(event, 'click'); }, true);
  document.addEventListener('change', function (event) { dispatch(event, 'change'); }, true);
})();
